import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';

/**
 * Represents a single option in the IF/ELSE dialog.
 */
interface IfDialogOption {
    id: string;
    label: string;
    description: string;
}

/**
 * IF/ELSE Dialog Component.
 *
 * Provides a dialog for inserting FreeMarker conditional constructs.
 * The available options adapt based on the variable's dataType and the
 * dialog mode (insert / tableColumn / editChip).
 *
 * Usage:
 *   <mvs-te-if-dialog
 *     [(visible)]="showIfDialog"
 *     [mode]="'insert'"
 *     [varPath]="'customer.firstName'"
 *     [label]="'Vorname'"
 *     [dataType]="'string'"
 *     (onInsert)="handleInsert($event)"
 *     (onCancel)="handleCancel()">
 *   </mvs-te-if-dialog>
 */
@Component({
    selector: 'mvs-te-if-dialog',
    templateUrl: './te-if-dialog.component.html',
    styleUrls: ['./te-if-dialog.component.scss'],
    standalone: false
})
export class TeIfDialogComponent implements OnInit, OnChanges {

    @Input() visible: boolean = false;
    @Input() mode: 'insert' | 'tableColumn' | 'editChip' = 'insert';
    @Input() varPath: string;
    @Input() label: string;
    @Input() dataType: string;
    @Input() enumValues: string[];
    @Input() preselectedOption: string;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onInsert = new EventEmitter<{ code: string; condition: string; option: string }>();
    @Output() onCancel = new EventEmitter<void>();

    availableOptions: IfDialogOption[] = [];
    selectedOption: string;
    equalsValue: string = '';
    generatedCode: string = '';
    dialogTitle: string = '';
    submitButtonLabel: string = 'Einfügen';

    ngOnInit(): void {
        this.buildOptions();
        this.updateDialogTitle();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['varPath'] || changes['dataType'] || changes['mode'] || changes['enumValues']) {
            this.buildOptions();
            this.updateDialogTitle();
        }

        if (changes['visible'] && this.visible) {
            this.buildOptions();
            this.updateDialogTitle();

            // Apply preselected option in edit mode
            if (this.preselectedOption) {
                this.selectedOption = this.preselectedOption;
            } else {
                this.selectedOption = null;
            }
            this.equalsValue = '';
            this.updatePreview();
        }
    }

    /**
     * Build the list of available options based on mode and dataType.
     */
    private buildOptions(): void {
        this.availableOptions = [];

        // "No Condition" — only in tableColumn and editChip modes
        if (this.mode === 'tableColumn' || this.mode === 'editChip') {
            this.availableOptions.push({
                id: 'none',
                label: 'Keine Bedingung',
                description: 'Spalte immer anzeigen / Bedingung entfernen'
            });
        }

        // "Exists / Has Value" — always shown
        this.availableOptions.push({
            id: 'exists',
            label: 'Vorhanden / Hat Wert',
            description: `Nur anzeigen wenn ${this.label || ''} einen Wert hat`
        });

        // "If / Else" — always shown
        this.availableOptions.push({
            id: 'ifElse',
            label: 'Wenn / Sonst',
            description: `Unterschiedlicher Inhalt abhängig davon ob ${this.label || ''} einen Wert hat`
        });

        // "If TRUE" — only for boolean
        if (this.dataType === 'boolean') {
            this.availableOptions.push({
                id: 'ifTrue',
                label: 'Wenn WAHR',
                description: `Anzeigen wenn ${this.label || ''} aktiv ist, Alternative wenn inaktiv`
            });
        }

        // "If = Enum Value" — only for enum with values
        if (this.dataType === 'enum' && this.enumValues && this.enumValues.length > 0) {
            for (const value of this.enumValues) {
                this.availableOptions.push({
                    id: `enum_${value}`,
                    label: `Wenn = "${value}"`,
                    description: `Anzeigen wenn ${this.label || ''} gleich "${value}" ist`
                });
            }
        }

        // "If Equals Value" — for string, currency, date
        if (this.dataType === 'string' || this.dataType === 'currency' || this.dataType === 'date') {
            this.availableOptions.push({
                id: 'equals',
                label: 'Wenn gleich Wert...',
                description: 'Vergleichen mit:'
            });
        }
    }

    /**
     * Update the dialog title based on the label.
     */
    private updateDialogTitle(): void {
        this.dialogTitle = `Bedingung für ${this.label || 'Variable'}`;
        this.submitButtonLabel = this.mode === 'editChip' ? 'Übernehmen' : 'Einfügen';
    }

    /**
     * Select an option and update the preview.
     */
    selectOption(optionId: string): void {
        this.selectedOption = optionId;
        this.updatePreview();
    }

    /**
     * Update the generated code preview based on the selected option.
     */
    updatePreview(): void {
        if (!this.selectedOption || !this.varPath) {
            this.generatedCode = '';
            return;
        }

        const path = this.varPath;

        switch (this.selectedOption) {
            case 'none':
                this.generatedCode = `\${${path}}`;
                break;

            case 'exists':
                this.generatedCode = `<#if ${path}??>\n  \${${path}}\n</#if>`;
                break;

            case 'ifElse':
                this.generatedCode = `<#if ${path}??>\n  \${${path}}\n<#else>\n  [kein Wert]\n</#if>`;
                break;

            case 'ifTrue':
                this.generatedCode = `<#if ${path}>\n  ...wenn aktiv...\n<#else>\n  ...wenn inaktiv...\n</#if>`;
                break;

            case 'equals':
                const val = this.equalsValue || '';
                this.generatedCode = `<#if ${path} == "${val}">\n  ...\n<#else>\n  ...\n</#if>`;
                break;

            default:
                // Handle enum options (id starts with "enum_")
                if (this.selectedOption.startsWith('enum_')) {
                    const enumVal = this.selectedOption.substring(5);
                    this.generatedCode = `<#if ${path} == "${enumVal}">\n  ...\n<#else>\n  ...\n</#if>`;
                } else {
                    this.generatedCode = '';
                }
                break;
        }
    }

    /**
     * Build the condition expression string for the selected option.
     */
    private buildConditionExpression(): string {
        if (!this.varPath) {
            return '';
        }

        const path = this.varPath;

        switch (this.selectedOption) {
            case 'none':
                return '';
            case 'exists':
            case 'ifElse':
                return `${path}??`;
            case 'ifTrue':
                return path;
            case 'equals':
                return `${path} == "${this.equalsValue || ''}"`;
            default:
                if (this.selectedOption.startsWith('enum_')) {
                    const enumVal = this.selectedOption.substring(5);
                    return `${path} == "${enumVal}"`;
                }
                return '';
        }
    }

    /**
     * Handle dialog submission.
     */
    handleSubmit(): void {
        if (!this.selectedOption) {
            return;
        }

        this.onInsert.emit({
            code: this.generatedCode,
            condition: this.buildConditionExpression(),
            option: this.selectedOption
        });

        this.visible = false;
        this.visibleChange.emit(false);
    }

    /**
     * Handle dialog cancellation.
     */
    handleCancel(): void {
        this.visible = false;
        this.visibleChange.emit(false);
        this.onCancel.emit();
    }

    /**
     * Handle visibility change from the p-dialog.
     */
    onVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
        if (!value) {
            this.onCancel.emit();
        }
    }
}
